import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Icon, F0TagStatus, TagStatusProps } from '@factorialco/f0-react'
import { Add, AlertCircleLine, CreditCard, ExternalLink } from '@factorialco/f0-react/icons/app'
import { EnvironmentContext } from 'config/environment'
import {
  BankingValueObjectsOnboardingStatusEnum,
  BenefitBankAccountDetailsFragment,
  BenefitBankAccountOnboardingDetailsFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { getAccountUrl } from 'modules/banking/components/AccountOnboarding/utils'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

type CardCountBadgeProps = {
  count: number
  label: string
}

interface LegalEntity {
  id: string
  legalName: string
  bankAccount: BenefitBankAccountDetailsFragment | null
  onboarding: BenefitBankAccountOnboardingDetailsFragment | null
}

type CardProps = {
  key?: React.Key
  legalEntity: LegalEntity
  toggleAddFundsModal: (bankAccount: BenefitBankAccountDetailsFragment) => void
  locale: string
  currency: string
}

export const Card = ({ legalEntity, toggleAddFundsModal, locale, currency }: CardProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const environment = React.useContext(EnvironmentContext)

  const CardCountBadge = ({ count, label }: CardCountBadgeProps) => (
    <Box
      paddingTop="s2"
      paddingBottom="s2"
      paddingLeft="s8"
      paddingRight="s8"
      borderRadius={{
        all: 'abs012',
      }}
      border={{ all: { color: 'grey300', width: 's1', style: 'solid' } }}
      alignItems="center"
      justifyContent="center"
    >
      <Text.Normal weight="semibold" size="100" color="grey1000">
        {count} · {label}
      </Text.Normal>
    </Box>
  )

  const countCardsByType = (
    bankAccount: BenefitBankAccountDetailsFragment | null,
    cardType: string
  ) => {
    return (
      bankAccount?.cardholdersConnection?.edges?.reduce((totalCards, cardholderEdge) => {
        const filteredCards = cardholderEdge.node.cards.filter(
          (card) => card.creationSource === cardType
        )
        return totalCards + filteredCards.length
      }, 0) || 0
    )
  }

  const getStatusTagConfig = (
    status: BankingValueObjectsOnboardingStatusEnum | undefined,
    accountLastDigits: string
  ): TagStatusProps => {
    {
      i18n.t('benefits.homepage.bank_account.cards_account')
    }

    switch (status) {
      case 'completed':
        return {
          variant: 'positive',
          text: accountLastDigits,
        }
      case 'missing_information':
        return {
          variant: 'warning',
          text: i18n.t('benefits.homepage.bank_account.status.missing_information'),
        }
      case 'rejected':
        return {
          variant: 'critical',
          text: i18n.t('benefits.homepage.bank_account.status.rejected'),
        }
      case 'pending':
        return {
          variant: 'neutral',
          text: i18n.t('benefits.homepage.bank_account.status.pending'),
        }
      case 'verification_pending':
        return {
          variant: 'info',
          text: i18n.t('benefits.homepage.bank_account.status.verification_pending'),
        }
      case 'in_review':
        return {
          variant: 'info',
          text: i18n.t('benefits.homepage.bank_account.status.in_review'),
        }
      default:
        return {
          variant: 'neutral',
          text: i18n.t('benefits.homepage.bank_account.status.no_account'),
        }
    }
  }

  const getPrimaryAction = (
    bankAccount: BenefitBankAccountDetailsFragment | null,
    onboarding: BenefitBankAccountOnboardingDetailsFragment | null,
    legalEntityId: string
  ) => {
    if (bankAccount) {
      return {
        label: i18n.t('benefits.homepage.bank_account.primary_action.add_money'),
        onClick: () => toggleAddFundsModal(bankAccount),
      }
    }

    // Incomplete benefits onboarding
    if (onboarding) {
      return {
        icon: Add,
        label: i18n.t('benefits.homepage.bank_account.primary_action.continue'),
        onClick: () => {
          window.open(getAccountUrl(environment, 'benefits', legalEntityId), '_blank')
        },
      }
    }

    return undefined
  }

  const getSecondaryActions = (
    bankAccount: BenefitBankAccountDetailsFragment | null,
    onboarding: BenefitBankAccountOnboardingDetailsFragment | null,
    legalEntityId: string
  ) => {
    // Bank account present
    if (bankAccount) {
      return [
        {
          icon: ExternalLink,
          label: i18n.t('benefits.homepage.bank_account.secondary_action.view_account'),
          onClick: () => {
            track(trackingEvents.benefits.openBankAccount)
            navigate(
              navigation.finance.banking.accounts.accountId.transactions.fullPath({
                id: bankAccount.id,
              })
            )
          },
        },
      ]
    }

    // No bank account and no onboarding
    if (!onboarding) {
      return [
        {
          icon: Add,
          label: i18n.t('benefits.homepage.bank_account.secondary_action.create_account'),
          onClick: () => {
            window.open(getAccountUrl(environment, 'benefits', legalEntityId), '_blank')
          },
        },
      ]
    }

    return []
  }

  const bankAccount = legalEntity.bankAccount
  const onboarding = legalEntity.onboarding

  const numberOfBenefitCards = countCardsByType(bankAccount, 'salary')
  const numberOfExpensesCards = countCardsByType(bankAccount, 'expenses')

  const lastDigits = `•••• ${bankAccount?.accountNumber?.substring(
    bankAccount?.accountNumber?.length - 4
  )}`

  // Bank accounts are created only after the onboarding is completed
  const bankStatus = bankAccount ? 'completed' : onboarding?.status
  const statusTagConfig = getStatusTagConfig(bankStatus, lastDigits)

  const primaryAction = getPrimaryAction(bankAccount, onboarding, legalEntity.id)
  const secondaryActions = getSecondaryActions(bankAccount, onboarding, legalEntity.id)

  return (
    <F0Card
      primaryAction={primaryAction}
      {...(secondaryActions.length > 0 && { secondaryActions })}
    >
      <Box gap="s4" paddingBottom="s20">
        <Text.Normal color="grey1000">{legalEntity.legalName}</Text.Normal>
        <Text.Normal weight="semibold" size="400" color="grey1000">
          {formattedAmount({
            amountCents: bankAccount?.availableBalanceCents ?? 0,
            currency,
            locale,
          })}
        </Text.Normal>
      </Box>

      <Box gap="s8">
        <Box flexDirection="row" gap="s4">
          <F0Icon icon={AlertCircleLine} size="lg" className="text-f1-icon" />
          <>
            <F0TagStatus variant={statusTagConfig.variant} text={statusTagConfig.text} />
          </>
        </Box>

        <Box flexDirection="row" gap="s4">
          <F0Icon icon={CreditCard} size="lg" className="text-f1-icon" />
          <Box flexDirection="row" gap="s4">
            {numberOfBenefitCards > 0 && (
              <CardCountBadge
                count={numberOfBenefitCards}
                label={i18n.t('benefits.homepage.bank_account.card_count.benefits')}
              />
            )}
            {numberOfExpensesCards > 0 && (
              <CardCountBadge
                count={numberOfExpensesCards}
                label={i18n.t('benefits.homepage.bank_account.card_count.expenses')}
              />
            )}
            {numberOfBenefitCards === 0 && numberOfExpensesCards === 0 && (
              <CardCountBadge
                count={0}
                label={i18n.t('benefits.homepage.bank_account.card_count.cards')}
              />
            )}
          </Box>
        </Box>
      </Box>
    </F0Card>
  )
}
